"use client";

import { useRef, useState } from "react";
import { motion, useMotionValue, useMotionValueEvent, useTransform, useIsomorphicLayoutEffect } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import { Check } from "lucide-react";

import type { PageHomeJourneyStep } from "@/sanity/pages/PageHomeTemplate";
import JourneyMediaFrame, { Artwork, journeyAsset, u, type JourneyTimeline } from "./JourneyMediaFrame";
import { journeyAmount, journeyCopy } from "./journeyMediaCopy";
import { parseNumber } from "./journeyMediaNumbers";
import base from "./journeyMedia.module.scss";
import styles from "./journeyChallenge.module.scss";

const BARS = [46, 70, 34, 62, 84, 28, 58, 78, 40, 66, 52, 88, 36, 60];
const LINE = "M1.25 24.0056C9.05 24.0056 9.05 41.0722 16.85 41.0722C23.05 41.0722 23.05 32.5389 29.35 32.5389C35.55 32.5389 35.55 38.2278 41.85 38.2278C49.65 38.2278 49.65 15.4722 57.45 15.4722C62.05 15.4722 62.05 18.3167 66.75 18.3167C71.45 18.3167 71.45 12.6278 76.15 12.6278C80.85 12.6278 80.85 26.85 85.45 26.85C91.75 26.85 91.75 24.0056 97.95 24.0056C105.75 24.0056 105.75 66.6722 113.55 66.6722C119.85 66.6722 119.85 83.7389 126.05 83.7389C132.25 83.7389 132.25 63.8278 138.55 63.8278C147.85 63.8278 147.85 52.45 157.25 52.45C165.05 52.45 165.05 49.6056 172.85 49.6056C177.55 49.6056 177.55 75.2056 182.25 75.2056C188.45 75.2056 188.45 92.2722 194.65 92.2722C200.95 92.2722 200.95 97.9611 207.15 97.9611C213.45 97.9611 213.45 126.406 219.65 126.406C225.85 126.406 225.85 129.25 232.15 129.25C236.85 129.25 236.85 106.494 241.45 106.494C246.15 106.494 246.15 109.339 250.85 109.339C257.05 109.339 257.05 83.7389 263.35 83.7389C269.55 83.7389 269.55 66.6722 275.85 66.6722C282.05 66.6722 282.05 58.1389 288.25 58.1389C294.55 58.1389 294.55 24.0056 300.75 24.0056C307.05 24.0056 307.05 1.25 313.25 1.25";

function Pointer({ x, y, label }: { x: number; y: number; label: string }) {
  const cleanLabel = stegaClean(label);
  return <g transform={`translate(${x} ${y})`}>
    <image href={journeyAsset("cursor.svg")} width="28" height="30" />
    <rect x="20" y="26" width={cleanLabel.length * 8 + 16} height="24" rx="4" fill="#8fedb8" />
    <text x="28" y="42" fill="#05211a" fontSize="14" fontWeight="500">{cleanLabel}</text>
  </g>;
}

function Gauge({ timeline, value, complete, you, passed, locale }: { timeline: JourneyTimeline; value: number; complete: boolean; you: string; passed: string; locale: string }) {
  const copyProgress = useTransform(timeline.progress, [0, 0.1, 0.72, 1], [0, 0, 1, 1]);
  const fill = useTransform(copyProgress, (p) => complete ? 1 : p);
  const count = useTransform(fill, (p) => `$${journeyAmount(Math.round(value * p), locale)}`);
  const dash = useTransform(fill, (p) => 1 - p);
  const pointX = useTransform(fill, (p) => 175 - Math.cos(p * Math.PI) * 134.4);
  const pointY = useTransform(fill, (p) => 235 - Math.sin(p * Math.PI) * 134.4);
  return <>
    <svg className={styles.gauge} viewBox="0 0 360 300">
      <path d="M40.6 235 A134.4 134.4 0 0 1 309.4 235" fill="none" stroke="#ffffff1a" strokeWidth="11.2" />
      <motion.path d="M40.6 235 A134.4 134.4 0 0 1 309.4 235" fill="none" stroke="#78e6ad" strokeWidth="11.2" strokeLinecap="round" pathLength="1" strokeDasharray="1 1" style={{ strokeDashoffset: dash }} />
      <motion.g style={{ x: pointX, y: pointY }}><circle r="6" fill="white" /><circle r="13" fill="#8fedb81a" /></motion.g>
    </svg>
    {complete ? <div className={styles.completeValue}>{passed}<span><Check aria-hidden="true" /></span></div> : <motion.span className={styles.gaugeValue}>{count}</motion.span>}
    {!complete && <svg className={styles.pointerLayer} viewBox="0 0 360 300"><motion.g style={{ x: pointX, y: pointY }}><Pointer x={-4} y={5} label={you} /></motion.g></svg>}
  </>;
}

function Drawdown({ timeline, you }: { timeline: JourneyTimeline; you: string }) {
  const path = useRef<SVGPathElement>(null);
  const reveal = useTransform(timeline.progress, [0, 0.12, 0.72, 1], [0, 0, 1, 1]);
  const offset = useTransform(reveal, (p) => 1 - p);
  const x = useMotionValue(1.25);
  const y = useMotionValue(24);
  useMotionValueEvent(reveal, "change", (p) => {
    const line = path.current;
    if (!line) return;
    const point = line.getPointAtLength(line.getTotalLength() * p);
    x.set(point.x); y.set(point.y);
  });
  const tooltipOpacity = useTransform(timeline.progress, [0, 0.72, 0.82, 1], [0, 0, 1, 1]);
  return <>
    <svg className={styles.drawdown} viewBox="0 0 360 300">
      <defs><linearGradient id="journeyDrawdown" x1="0" y1="0" x2="1" y2="0"><stop stopColor="#8fedb8" stopOpacity="0.3" /><stop offset="1" stopColor="#8fedb8" /></linearGradient></defs>
      <path d="M23 249H336" stroke="#d39c8b" strokeOpacity="0.45" strokeDasharray="3 5" />
      <path d="M23 136H336 M23 188H336" stroke="#ffffff0a" />
      <g transform="translate(22 110)">
        <motion.path ref={path} d={LINE} fill="none" stroke="url(#journeyDrawdown)" strokeWidth="2.5" strokeLinecap="round" pathLength="1" strokeDasharray="1 1" style={{ strokeDashoffset: offset }} />
        <motion.g style={{ x, y }}><circle r="5" fill="#caffdb" /><circle r="12" fill="#8fedb81a" /><Pointer x={0} y={0} label={you} /></motion.g>
      </g>
      <motion.g style={{ opacity: tooltipOpacity }}><path d="m306 73 5 5 9-10" fill="none" stroke="#8fedb8" strokeWidth="2" /></motion.g>
    </svg>
  </>;
}

function RiskBar({ height, i, progress }: { height: number; i: number; progress: JourneyTimeline["progress"] }) {
  const scaleY = useTransform(progress, [0, 0.1 + i * 0.032, 0.29 + i * 0.032, 1], [0.04, 0.04, 1, 1]);
  return <span><motion.i style={{ height: u(height), scaleY, background: i === 11 ? "#dbffe5" : undefined }} /></span>;
}

function ChipRail({ chips, index, timeline }: { chips: string[]; index: number; timeline: JourneyTimeline }) {
  const rail = useRef<HTMLDivElement>(null);
  const [centers, setCenters] = useState<number[]>([]);
  useIsomorphicLayoutEffect(() => {
    if (!rail.current) return;
    const measure = () => setCenters(Array.from(rail.current!.children).map((child) => {
      const element = child as HTMLElement;
      return element.offsetLeft + element.offsetWidth / 2;
    }));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(rail.current);
    return () => observer.disconnect();
  }, []);
  const x = useTransform(timeline.progress, (p) => {
    const to = centers[index] ?? 0;
    const from = centers[index === 0 ? 0 : index - 1] ?? to;
    const arrival = timeline.reducedMotion ? 1 : 1 - (1 - Math.min(p / 0.16, 1)) ** 3;
    return -(from + (to - from) * arrival);
  });
  const opacity = useTransform(timeline.progress, [0, 0.08, 0.92, 1], [index === 0 ? 0 : 1, 1, 1, index === 3 ? 0 : 1]);
  return <motion.div className={styles.rail} style={{ opacity: timeline.reducedMotion ? 1 : opacity }}>
    <motion.div ref={rail} className={styles.chips} style={{ x }}>
      {chips.map((chip, i) => <span key={i} className={i === index ? styles.active : undefined}>{chip}{i === 3 && <Check aria-hidden="true" />}</span>)}
    </motion.div>
  </motion.div>;
}

function ChallengeScene({ step, language, index, timeline }: { step: PageHomeJourneyStep; language?: string; index: number; timeline: JourneyTimeline }) {
  const copy = journeyCopy(language);
  const values = ["8%", "6%", "1%"].map((fallback, i) => step.mediaStats?.[i]?.value || fallback);
  const labels = copy.labels.map((fallback, i) => step.mediaStats?.[i]?.label || fallback);
  const chips = copy.chips.map((fallback, i) => step.mediaChips?.[i] || (i === 3 && step.mediaCardTitle) || fallback);
  const account = step.mediaAccountSize ?? 100000;
  const target = parseNumber(values[0])?.target ?? 8;
  const entrance = useTransform(timeline.progress, [0, 0.12, 0.87, 1], [0, 1, 1, 0]);
  const opacity = useTransform(entrance, [0, 1], [0.25, 1]);
  const y = useTransform(entrance, [0, 1], [10, 0]);
  const barCursor = useTransform(timeline.progress, [0, 0.2, 0.76, 1], [26, 26, 286, 286]);
  const sceneOpacity = timeline.reducedMotion ? 1 : opacity;
  return <>
    {[[106, 76, 244], [118, 62, 272], [154, 76, 244], [142, 62, 272]].map(([left, top, height], i) => <div key={i} className={base.challengeLayer} style={{ left: u(left), top: u(top), height: u(height) }} />)}
    <div className={base.challengeCard}>
      <div className={base.challengeHeader}><Artwork name="logo-white.svg" /><span>{labels[index === 3 ? 0 : index]}</span><span className={base.account}>{journeyAmount(account / 1000, copy.locale)}k</span></div>
      <motion.div className={styles.scene} style={{ opacity: sceneOpacity, y: timeline.reducedMotion ? 0 : y }}>
        {index === 0 || index === 3 ? <>
          <Gauge timeline={timeline} value={account * target / 100} complete={index === 3} you={step.mediaYouLabel || copy.you} passed={values[0]} locale={copy.locale} />
          {index === 3 && <p className={styles.passed}>{step.mediaPassedLabel || copy.passed}</p>}
        </> : index === 1 ? <><p className={base.chartValue}>{values[1]}</p><Drawdown timeline={timeline} you={step.mediaYouLabel || copy.you} /></> : <>
          <p className={base.chartValue}>{values[2]}</p><div className={base.chartGrid} /><Artwork name="loss-limit.svg" className={base.lossLimit} />
          <div className={styles.bars}>{BARS.map((height, i) => <RiskBar key={i} height={height} i={i} progress={timeline.progress} />)}</div>
          <p className={styles.under}>{step.mediaUnderLabel || copy.under} {values[2]}</p>
          <svg className={styles.pointerLayer} viewBox="0 0 360 300"><motion.g style={{ x: barCursor, y: 220 }}><Pointer x={0} y={0} label={step.mediaYouLabel || copy.you} /></motion.g></svg>
        </>}
      </motion.div>
    </div>
    <ChipRail chips={chips} index={index} timeline={timeline} />
    <div className={styles.objectives}>{[0, 1, 2].map((i) => <span key={i} data-done={index > i} data-current={index === i}>{index > i ? <Check aria-hidden="true" /> : <i />}</span>)}</div>
  </>;
}

export default function JourneyChallengeMedia({ step, language, className }: { step: PageHomeJourneyStep; language?: string; className?: string }) {
  return <JourneyMediaFrame variant="challenge" language={language} length={4} durations={[3900, 4200, 3800, 2400]} className={className} description={step.mediaCardSubtitle}>
    {(index, timeline) => <ChallengeScene step={step} language={language} index={index} timeline={timeline} />}
  </JourneyMediaFrame>;
}
